// Demo page for @liaison/web: connect, watch the transcript, type a turn,
// inject context, suspend the session and tap to resume it (SPEC §10), and
// read the raw control frames.
//
// Everything above `bootstrap()` is page-independent, so test/demo.test.ts can
// drive the button modes and the resume gesture without a browser; everything
// below it touches the demo DOM and therefore runs only on the demo page.

import {
	Liaison,
	type AudioDeviceInfo,
	type AudioDevices,
	type InputLevel,
	type Session,
	type Status,
	type SuspendReason,
	type ToolDefinition,
} from "../src/index";
import type { Config, Frame } from "../src/protocol";
import { createTranscriptView, type TranscriptView } from "./transcript";

/**
 * What the buttons mean right now. `idle` is no session — never started, or
 * ended; `live` is a session on the wire, including the connecting and
 * reconnecting edges; `suspended` is SPEC §10: released server-side, media
 * gone, the object still in hand and one tap from being back.
 */
export type Mode = "idle" | "live" | "suspended";

/** The controls whose enabled state is a function of the mode. */
export interface Controls {
	connect: HTMLButtonElement;
	suspend: HTMLButtonElement;
	resume: HTMLButtonElement;
	end: HTMLButtonElement;
	text: HTMLInputElement;
	send: HTMLButtonElement;
	inject: HTMLInputElement;
	injectSend: HTMLButtonElement;
	/**
	 * Wake on speech. `ConnectOptions.wake` is read once, when the `Session` is
	 * constructed, so unlike the idle window — which is re-read on every mint —
	 * toggling this mid-session would do nothing: it is live exactly when a new
	 * session could be started.
	 */
	wake: HTMLInputElement;
	/** Tags the moment in the session trace (SPEC §12), label `bad`. */
	mark: HTMLButtonElement;
	/** Free text that rides on the next mark, and is kept for the one after. */
	markNote: HTMLInputElement;
}

/**
 * The overnight tally of `docs/wake-on-speech.md` §10 step 4: how many times
 * the room woke the session and how many of those wakes produced a committed
 * turn. The wake count nobody asked for is the feature's real acceptance
 * criterion, and an overnight run outlives both the 200-line frame log and any
 * reload, so it is kept in `localStorage` rather than on the page.
 */
export interface WakeCounts {
	/** Wakes the local detector triggered. */
	wakes: number;
	/** `transcript.user` finals seen since the count started. */
	turns: number;
	/** When counting started, and when the last wake fired; epoch ms, 0 for never. */
	since: number;
	lastWakeAt: number;
}

export const WAKE_COUNTS_KEY = "liaison.demo.wakeCounts";

/**
 * Reads the tally back. A missing, truncated or hand-edited entry counts as a
 * fresh night rather than throwing on load: losing the count is bad, but a
 * page that will not start at all loses every future one too.
 */
export function loadWakeCounts(store: Pick<Storage, "getItem">): WakeCounts {
	const fresh: WakeCounts = { wakes: 0, turns: 0, since: Date.now(), lastWakeAt: 0 };
	let raw: string | null = null;
	try {
		raw = store.getItem(WAKE_COUNTS_KEY);
	} catch {
		return fresh; // storage disabled (private mode, third-party iframe)
	}
	if (!raw) return fresh;
	try {
		const held = JSON.parse(raw) as Partial<WakeCounts>;
		return {
			wakes: Number.isFinite(held.wakes) ? Number(held.wakes) : 0,
			turns: Number.isFinite(held.turns) ? Number(held.turns) : 0,
			since: Number.isFinite(held.since) ? Number(held.since) : fresh.since,
			lastWakeAt: Number.isFinite(held.lastWakeAt) ? Number(held.lastWakeAt) : 0,
		};
	} catch {
		return fresh;
	}
}

/** The mode a reported status puts the page in. */
export function modeFor(status: Status | "idle" | "error"): Mode {
	switch (status) {
		case "suspended":
			return "suspended";
		case "closed":
		case "idle":
		case "error":
			return "idle";
		default:
			// connecting, reconnecting, listening, thinking, speaking: the
			// session is on the wire, or the SDK is putting it back on.
			return "live";
	}
}

/**
 * Enables exactly the controls that can do something in `mode`. `held` says a
 * suspended Session object is still in hand: the conversation is resumable
 * even though nothing is connected, which is the whole point of §10, so resume
 * and end stay live while connect does not.
 */
export function applyMode(c: Controls, mode: Mode, held = false): void {
	const live = mode === "live";
	const resumable = mode === "suspended" || held;
	c.connect.disabled = live || resumable;
	c.suspend.disabled = !live;
	c.resume.disabled = !resumable;
	c.end.disabled = !live && !resumable;
	// A suspended session has no media and no server-side turn taking: a typed
	// turn or an injected note would have nowhere to go until it is resumed.
	c.text.disabled = !live;
	c.send.disabled = !live;
	c.inject.disabled = !live;
	c.injectSend.disabled = !live;
	// Config, not a gesture: editable exactly while connect is.
	c.wake.disabled = live || resumable;
	// A mark only means something while a session is running: there is no
	// moment to tag in a session that has been released.
	c.mark.disabled = !live;
	c.markNote.disabled = !live;
}

/**
 * The tap-to-resume gesture (SPEC §10). The transcript is deliberately left on
 * screen: a resume continues the same conversation and the SDK re-sends
 * `s.history`, so clearing would throw away the words the user is looking at
 * and then have the server replay them straight back. A rejection leaves the
 * session suspended with its history intact — the second tap is the retry — so
 * the failure is reported and nothing else changes.
 */
export async function tapResume(
	s: Pick<Session, "resume">,
	view: TranscriptView,
	report: (text: string) => void,
): Promise<boolean> {
	report("· resume: minting a fresh token and re-sending history");
	try {
		await s.resume();
		report("· resumed");
		return true;
	} catch (err) {
		// Still suspended, still holding the conversation: say so and let the
		// user tap again rather than dropping them back to connect.
		view.line("error", "error", `resume failed, still suspended: ${err}`);
		report(`· resume failed: ${err}`);
		return false;
	}
}

/** The client-suspend gesture: `session.suspend` → `session.suspended{reason:"client"}`. */
export async function tapSuspend(
	s: Pick<Session, "suspend">,
	view: TranscriptView,
	report: (text: string) => void,
): Promise<boolean> {
	report("· suspend requested (reason will be \"client\")");
	try {
		await s.suspend();
		return true;
	} catch (err) {
		view.line("error", "error", `suspend failed: ${err}`);
		report(`· suspend failed: ${err}`);
		return false;
	}
}

/**
 * Paints the mute toggle from the two facts that decide it: whether a `Session`
 * object is in hand at all, and what its gate is currently doing. `mute()` is
 * local and synchronous — no frame, no round trip — so the button is a plain
 * reflection of `session.muted` and never has an in-flight state.
 *
 * It is deliberately live for a *suspended* session as well as a connected
 * one: the flag outlives the media, and arriving at a resume already muted is
 * the whole reason the SDK re-applies it on every fresh capture.
 *
 * `data-muted` carries the state for CSS and for a headless run; the label is
 * the verb the next press performs, which is the convention the rest of this
 * page's buttons follow.
 */
export function applyMuteButton(btn: HTMLButtonElement, muted: boolean, held: boolean): void {
	btn.disabled = !held;
	btn.textContent = muted ? "unmute" : "mute";
	btn.dataset["muted"] = muted ? "1" : "0";
	btn.setAttribute("aria-pressed", muted ? "true" : "false");
}

/** The bottom of the meter's scale; `InputLevel` clamps its dBFS there too. */
const METER_FLOOR_DB = -100;

/**
 * Where a dBFS reading sits on the bar, 0–100 %. The scale is linear in
 * decibels rather than in amplitude on purpose: an amplitude bar spends its
 * whole length in the top 6 dB and paints room noise as nothing at all, which
 * defeats both of the things this meter is for — seeing that the mic is
 * actually live, and seeing speech clear the detector's floor.
 *
 * Readings outside the promised [-100, 0] are clamped rather than trusted: a
 * silent quantum is -Infinity in dBFS before any floor is applied, and a
 * NaN must not become a `width: NaN%` that leaves the last frame on screen.
 */
export function levelPct(db: number): number {
	if (!Number.isFinite(db)) return db === Infinity ? 100 : 0;
	if (db <= METER_FLOOR_DB) return 0;
	if (db >= 0) return 100;
	return ((db - METER_FLOOR_DB) / -METER_FLOOR_DB) * 100;
}

/** How far over the tracked floor counts as speech rather than as the room. */
const TALKING_OVER_FLOOR_DB = 6;

/**
 * The "muted — you are talking" condition, and the reason `InputLevel` is
 * measured in front of the gate: while muted the worklet still sees real
 * speech, so the page can say the one thing a user who has forgotten the
 * button needs to hear. The margin keeps a noisy room from tripping it — the
 * floor is the detector's own estimate of that room.
 */
export function mutedButTalking(l: InputLevel | null): boolean {
	return (
		l !== null &&
		l.muted &&
		Number.isFinite(l.db) &&
		Number.isFinite(l.floorDb) &&
		l.db > l.floorDb + TALKING_OVER_FLOOR_DB
	);
}

/**
 * The "agent URL" field (SPEC §18.14), read for a mint: empty is no agent.
 * The URL carries the agent's own credential, so the page never shows it back
 * or logs it — only `host` — and a refusal quotes no part of it: not
 * `new URL`'s own TypeError, which would, and not the scheme either, which
 * for a pasted `s3cret:…` is the secret.
 */
export function agentField(raw: string): { url: string; host: string } | null {
	const url = raw.trim();
	if (!url) return null;
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		throw new Error("agent URL does not parse");
	}
	if (parsed.protocol !== "wss:" && parsed.protocol !== "ws:") {
		throw new Error("agent URL must be wss:// (or ws:// on a dev node)");
	}
	return { url, host: parsed.host };
}

/** One entry of a device picker: the `deviceId` to pass, and what to show. */
export interface DeviceOption {
	value: string;
	label: string;
}

/**
 * The options for one picker, system default first. An empty `value` is how
 * this page spells "no `inputDeviceId`", which is also what the SDK means by
 * `null`, so the default entry needs no special casing at the call site.
 *
 * Two browser facts are absorbed here. A device with an empty `label` has not
 * been named because the page holds no `getUserMedia` grant yet — that is the
 * browser's privacy rule and not something to work around, so it gets a
 * positional name and is re-enumerated after the grant. A device with an empty
 * `deviceId` is the pre-grant *placeholder*: it says a microphone exists
 * without saying which one, cannot be handed to `deviceId: {exact: …}`, and as
 * an option would be a second "system default" that behaves differently — so
 * it is dropped.
 */
export function deviceOptions(
	list: readonly AudioDeviceInfo[],
	kind: "input" | "output",
): DeviceOption[] {
	const opts: DeviceOption[] = [{ value: "", label: "system default" }];
	for (const d of list) {
		if (!d.deviceId) continue;
		opts.push({
			value: d.deviceId,
			label: d.label || `${kind === "input" ? "Microphone" : "Speaker"} ${opts.length}`,
		});
	}
	return opts;
}

const el = <T extends HTMLElement>(id: string): T => {
	const node = document.getElementById(id);
	if (!node) throw new Error(`missing #${id}`);
	return node as T;
};

const LOG_MAX = 200;

/** Wires the demo page. Called once, at the bottom, when that page is loaded. */
function bootstrap(): void {
	const badge = el("badge");
	const reason = el("reason");
	const sid = el("sid");
	const urlInput = el<HTMLInputElement>("url");
	const promptInput = el<HTMLTextAreaElement>("prompt");
	const voiceInput = el<HTMLInputElement>("voice");
	const firstInput = el<HTMLInputElement>("first");
	const resumeInput = el<HTMLInputElement>("resume-message");
	const idleInput = el<HTMLInputElement>("idle");
	const apiKeyInput = el<HTMLInputElement>("apikey");
	const agentInput = el<HTMLInputElement>("agent-url");
	const controls: Controls = {
		connect: el<HTMLButtonElement>("connect"),
		suspend: el<HTMLButtonElement>("suspend"),
		resume: el<HTMLButtonElement>("resume"),
		end: el<HTMLButtonElement>("end"),
		text: el<HTMLInputElement>("text"),
		send: el<HTMLButtonElement>("send"),
		inject: el<HTMLInputElement>("inject"),
		injectSend: el<HTMLButtonElement>("injectsend"),
		wake: el<HTMLInputElement>("wake"),
		mark: el<HTMLButtonElement>("mark"),
		markNote: el<HTMLInputElement>("marknote"),
	};
	const markCount = el("markcount");
	const transcript = el("transcript");
	const log = el("log");
	const jobs = el("jobs");
	const agentPane = el("agent");
	const note = el("note");
	const wakesPane = el("wakes");
	// The media controls. They are not part of `Controls`, whose members are the
	// ones `applyMode` switches on the session lifecycle: the pickers are live
	// in every mode (they choose the device the *next* session opens with as
	// well as switching a running one), and the mute button follows
	// `session.muted` rather than the mode.
	const muteBtn = el<HTMLButtonElement>("mute");
	const micSelect = el<HTMLSelectElement>("mic");
	const speakerSelect = el<HTMLSelectElement>("speaker");
	const speakerNote = el("speaker-note");
	const meterFill = el("meter-fill");
	const meterPeak = el("meter-peak");
	const meterFloor = el("meter-floor");
	const levelRead = el("level-read");
	const levelHint = el("level-hint");
	// Whether the next session passes `onLevel` at all: that is the only switch
	// there is (docs/sdk-web.md §7), and on a wake-less session it is also what
	// makes the SDK build an audio graph. Not a member of `Controls` — that is
	// the set `applyMode` switches — but it obeys the same rule as `wake`: read
	// once at connect, and therefore editable exactly while connect is.
	const meterCheck = el<HTMLInputElement>("meter");

	/**
	 * The overnight tally. Rendered from `localStorage` on load, written back on
	 * every wake and every committed turn: §10 step 4 is a run measured in
	 * hours, and the raw-frame log evicts past 200 lines long before morning.
	 */
	let counts = loadWakeCounts(localStorage);

	function renderCounts(): void {
		const last = counts.lastWakeAt
			? new Date(counts.lastWakeAt).toLocaleTimeString()
			: "never";
		const hours = (Date.now() - counts.since) / 3_600_000;
		const rate = hours > 0 ? (counts.wakes / hours).toFixed(2) : "0.00";
		const backlog = session?.wakeBacklogMs ?? 0;
		wakesPane.replaceChildren();
		const line = document.createElement("div");
		line.className = "n";
		line.textContent =
			`wakes ${counts.wakes} (${rate}/h, last ${last}) · ` +
			`committed turns ${counts.turns} · pre-roll backlog ${Math.round(backlog)} ms`;
		const started = document.createElement("div");
		started.textContent = `counting since ${new Date(counts.since).toLocaleString()}`;
		const reset = document.createElement("button");
		reset.textContent = "reset counters";
		reset.addEventListener("click", () => {
			counts = { wakes: 0, turns: 0, since: Date.now(), lastWakeAt: 0 };
			localStorage.setItem(WAKE_COUNTS_KEY, JSON.stringify(counts));
			renderCounts();
		});
		wakesPane.append(line, started, reset);
	}

	/**
	 * `wakeBacklogMs` is a field the worklet updates on its own clock, so a pane
	 * rendered only on wakes and commits freezes the number at its pre-drain
	 * value and reads as a backlog that never drains. It does drain, out of
	 * silence and only after the false-cut window, which takes seconds — so
	 * while there is one, follow it. The poll is as coarse as the worklet's own
	 * report (250 ms) and stops the moment the backlog is gone, the session is
	 * suspended, or the session is gone.
	 */
	// `window.setInterval`, not the bare one: on a page the handle is a number,
	// which saves naming a type for a value the platform already names.
	let backlogPoll: number | null = null;

	function followBacklog(): void {
		if (backlogPoll !== null) return;
		let seen = false;
		backlogPoll = window.setInterval(() => {
			const ms = session?.wakeBacklogMs ?? 0;
			if (ms > 0) seen = true;
			renderCounts();
			const done = !session || session.state === "suspended" || (seen && ms === 0);
			if (done && backlogPoll !== null) {
				window.clearInterval(backlogPoll);
				backlogPoll = null;
			}
		}, 250);
	}

	// Served by liaisond itself in the normal case; file:// falls back to the dev port.
	urlInput.value = location.protocol.startsWith("http")
		? `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/v1/session`
		: "ws://localhost:8080/v1/session";
	// The dev mint key (SPEC §8). `?apiKey=…` is how a headless run supplies it;
	// leaving the field empty is how the demo talks to a --no-auth node. It is a
	// *server* key, so the query parameter is dropped from the address bar the
	// moment it has been read: the page keeps it, the history does not.
	const params = new URLSearchParams(location.search);
	apiKeyInput.value = params.get("apiKey") ?? "";
	if (params.has("apiKey")) {
		params.delete("apiKey");
		const rest = params.toString();
		history.replaceState(
			null,
			"",
			location.pathname + (rest ? `?${rest}` : "") + location.hash,
		);
	}

	function logLine(text: string, cls?: string): void {
		const line = document.createElement("div");
		if (cls) line.className = cls;
		line.textContent = text;
		log.appendChild(line);
		while (log.childElementCount > LOG_MAX) log.firstElementChild?.remove();
		log.scrollTop = log.scrollHeight;
	}

	function logFrame(dir: "in" | "out", f: Frame): void {
		const { t, ...rest } = f as Frame & Record<string, unknown>;
		let body = JSON.stringify(rest);
		if (body.length > 300) body = `${body.slice(0, 300)}…`;
		logLine(`${dir === "out" ? "→" : "←"} ${t} ${body === "{}" ? "" : body}`, dir);
		// `state` carries the running-jobs snapshot and is re-emitted on every job
		// change, which is the only signal a job started or ended (SPEC §9).
		if (dir === "in" && t === "state") {
			renderJobs();
			renderAgent();
		}
	}

	/** `state.agent` (SPEC §18.14): the connection and the agent's last status. */
	function renderAgent(): void {
		const a = session?.agent;
		agentPane.textContent = a
			? `${a.connected ? "connected" : "not connected"}${a.status ? ` · ${a.status}` : ""}`
			: "none";
	}

	/**
	 * One client tool (SPEC §9), so the liaison can act on the page it is talking
	 * about. The handler is what `tool.call` runs; its return value is what the
	 * model reads back.
	 */
	const setNote: ToolDefinition = {
		schema: {
			name: "set_note",
			description: "Write a short note into the box the user is looking at.",
			parameters: {
				type: "object",
				properties: { text: { type: "string", description: "the note to show" } },
				required: ["text"],
			},
		},
		handler: async (args) => {
			const text = String((args as { text?: unknown } | null)?.text ?? "");
			note.textContent = text;
			return { ok: true, shown: text };
		},
	};

	/** The running-jobs snapshot from the latest `state` frame, one row per job. */
	function renderJobs(): void {
		const running = session?.jobs ?? [];
		jobs.replaceChildren();
		if (running.length === 0) {
			const none = document.createElement("div");
			none.className = "none";
			none.textContent = "none";
			jobs.appendChild(none);
			return;
		}
		for (const job of running) {
			const row = document.createElement("div");
			row.className = "job";
			const label = document.createElement("span");
			label.textContent = `${job.jobId} ${job.name}`;
			const cancel = document.createElement("button");
			cancel.textContent = "cancel";
			cancel.addEventListener("click", () => session?.cancelJob(job.jobId));
			row.append(label, cancel);
			jobs.appendChild(row);
		}
	}

	const view = createTranscriptView(transcript);

	function setStatus(s: Status | "idle" | "error"): void {
		badge.dataset["s"] = s;
		badge.textContent = s;
	}

	let session: Session | null = null;
	/**
	 * Marks pressed on this page load. Deliberately not persisted the way the
	 * wake tally is: a mark's value is in the trace file, and the count here is
	 * only so the person pressing it can see that it registered.
	 */
	let marks = 0;

	function setMode(mode: Mode): void {
		applyMode(controls, mode, session !== null && session.state === "suspended");
		// Read once at connect like `wake`, so it is editable exactly while
		// connect is — which `applyMode` has just decided.
		meterCheck.disabled = controls.connect.disabled;
		// Mute and the device choices are session facts, not mode facts, and a
		// status change is exactly when they can have moved underneath the page:
		// a §13 reconnect and a resume both re-acquire capture, and the SDK
		// re-applies the gate and the sticky ids to it — or falls back to the
		// system default when a sticky id can no longer be acquired, which the
		// pickers have to show rather than keep claiming the vanished device.
		applyMuteButton(muteBtn, session?.muted ?? false, session !== null);
		syncPickers();
		// A resume re-acquires capture and rebuilds the graph, so the meter loop
		// — which parks itself whenever there is no reading to paint — is put
		// back on its feet here as well as at connect.
		if (session !== null) startLevelPaint();
	}

	muteBtn.addEventListener("click", () => {
		const s = session;
		if (!s) return;
		// Synchronous and local: there is nothing to await and nothing to undo if
		// the wire is down, so the button is repainted from the session right
		// here instead of waiting for a status change that may never come.
		s.setMuted(!s.muted);
		applyMuteButton(muteBtn, s.muted, true);
		logLine(
			s.muted
				? "· muted — local gate, no frame sent; the server hears exact silence"
				: "· unmuted",
		);
	});

	/**
	 * The meter is painted from `requestAnimationFrame` reading `s.inputLevel`,
	 * not from inside `onLevel`. The callback fires on the report cadence — 100
	 * ms by default — regardless of what the display is doing: re-rendering
	 * there buys layout work nobody sees and a bar the eye can watch stepping.
	 * rAF paints once per displayed frame, coalesces a burst of reports into the
	 * one that is current, and is not called at all while the tab is hidden.
	 * `onLevel` is passed only because its presence is what turns monitoring on.
	 */
	let levelFrame: number | null = null;
	/**
	 * The empty-state text currently on the readout, or `null` when a reading
	 * is. Without it the idle path rewrites three identical style strings and an
	 * identical `textContent` — and an identical assignment still replaces the
	 * text node and dirties layout, which is not free at 60 Hz for the hours an
	 * overnight false-wake run (docs/wake-on-speech.md §10 step 4) spends
	 * suspended. It holds the text rather than a flag because there are two
	 * empty states — no graph, and no `onLevel` to report through one — and a
	 * connect is where the page moves between them.
	 */
	let levelIdleText: string | null = null;
	/**
	 * Whether the session now in hand was connected with `onLevel`. Decided at
	 * connect from `#meter`, because that is when the SDK reads it: with it off
	 * nothing will ever report, so the meter shows its empty state and the
	 * paint loop is never started.
	 */
	let levelMonitored = false;
	/** The slow watch for a graph appearing; see `waitForLevel`. */
	let levelWatch: number | null = null;

	function renderLevel(l: InputLevel | null): void {
		if (l === null) {
			// No report yet, or no graph: a session without `wake` has no capture
			// while it is suspended, and a closed one has none at all. Say so,
			// rather than leaving the last reading up as if the mic were live —
			// once, and then not again until there is something else to show. A
			// live session that asked for no levels is the third case and a
			// different sentence: nothing is measuring and nothing is going to,
			// so the readout names that instead of blaming a missing graph.
			const text =
				session !== null && !levelMonitored
					? "input level — meter off (no onLevel on this session)"
					: "input level — no capture graph";
			if (levelIdleText === text) return;
			levelIdleText = text;
			meterFill.style.width = "0%";
			meterPeak.style.left = "0%";
			meterFloor.style.left = "0%";
			levelRead.textContent = text;
			levelHint.hidden = true;
			return;
		}
		levelIdleText = null;
		meterFill.style.width = `${levelPct(l.db)}%`;
		// `peakDb` is already a hold over the report interval, so the tick needs
		// no decay of its own: it is the loudest sample of the words just spoken.
		meterPeak.style.left = `${levelPct(l.peakDb)}%`;
		meterFloor.style.left = `${levelPct(l.floorDb)}%`;
		levelRead.textContent =
			`input ${l.db.toFixed(1)} dBFS · peak ${l.peakDb.toFixed(1)} · ` +
			`floor ${l.floorDb.toFixed(1)}${l.muted ? " · gated" : ""}`;
		levelHint.hidden = !mutedButTalking(l);
	}

	function paintLevel(): void {
		const l = session?.inputLevel ?? null;
		// Nothing to paint: the session is gone, or it is a wake-less session
		// whose graph was released at the suspend. Clear the meter once and stop
		// asking for frames — a display-rate loop over a null reading is pure
		// cost, and the overnight run sits in exactly this state for hours. The
		// condition is the *reading*, not the state: a `wake` session keeps its
		// graph through an idle suspend and must keep painting there.
		if (l === null) {
			levelFrame = null;
			renderLevel(null);
			waitForLevel();
			return;
		}
		renderLevel(l);
		levelFrame = requestAnimationFrame(paintLevel);
	}

	function startLevelPaint(): void {
		// A session that passed no `onLevel` has nothing to paint from, ever, so
		// the loop must never start rather than start and park on its first null
		// reading: that would cost a frame and arm a `waitForLevel` watch that
		// can never fire. The empty state is what that session gets instead.
		if (!levelMonitored) {
			renderLevel(null);
			return;
		}
		if (levelFrame === null) levelFrame = requestAnimationFrame(paintLevel);
	}

	/**
	 * Waits for a graph to exist, as coarsely as the worklet's own 250 ms report
	 * and writing nothing while it does not. This is what brings the meter back,
	 * and a status change is not enough on its own twice over: the first report
	 * of a fresh session lands ~100 ms *after* `connect` resolves, and a session
	 * can sit in `listening` for the whole gap between a resume and its first
	 * report without emitting another status. A closed session ends the watch —
	 * the next connect starts a new one.
	 */
	function waitForLevel(): void {
		// No session, no graph to wait for: a page sitting at idle holds no timer
		// at all, and the next connect starts the loop directly.
		if (levelWatch !== null || session === null) return;
		levelWatch = window.setInterval(() => {
			const waiting = session !== null && session.inputLevel === null;
			if (waiting) return; // a read, no DOM work
			if (levelWatch !== null) {
				window.clearInterval(levelWatch);
				levelWatch = null;
			}
			if (session !== null) startLevelPaint();
		}, 250);
	}

	/**
	 * `setSinkId` is Chromium's; Firefox and Safari ship the audio element
	 * without it, and there the sink is the OS's business. A control that
	 * silently does nothing is worse than no control, so the picker is disabled
	 * and the reason is on the page.
	 */
	const outputSupported = Liaison.outputDeviceSupported();
	if (!outputSupported) {
		speakerSelect.disabled = true;
		speakerNote.hidden = false;
	}

	function fillPicker(sel: HTMLSelectElement, opts: DeviceOption[], want: string): void {
		sel.replaceChildren();
		for (const o of opts) {
			const opt = document.createElement("option");
			opt.value = o.value;
			opt.textContent = o.label;
			sel.appendChild(opt);
		}
		// A wanted id that is no longer in the list is an unplugged device: the
		// SDK falls back to the system default and clears its own id, so the
		// picker lands on the same entry instead of showing a phantom choice.
		sel.value = opts.some((o) => o.value === want) ? want : "";
	}

	function fillPickers(d: AudioDevices): void {
		// While a session is up its ids are the truth — including a null that a
		// fallback just produced; with no session the page's own selection is,
		// because that is what the next `connect` will be asked to open with.
		const s = session;
		fillPicker(micSelect, deviceOptions(d.inputs, "input"), s ? (s.inputDeviceId ?? "") : micSelect.value);
		if (outputSupported) {
			fillPicker(
				speakerSelect,
				deviceOptions(d.outputs, "output"),
				s ? (s.outputDeviceId ?? "") : speakerSelect.value,
			);
		}
	}

	/**
	 * Re-enumerates. Labels are empty strings until the page holds a
	 * `getUserMedia` grant — the browser's privacy rule — so this runs at load,
	 * where the pickers get positional names, and again right after a connect,
	 * where the real names finally exist. A `devicechange` needs no call: the
	 * SDK hands the fresh list to `onDevices`.
	 */
	async function refreshDevices(why: string): Promise<void> {
		try {
			fillPickers(await Liaison.devices());
		} catch (err) {
			logLine(`· device list failed (${why}): ${err}`);
		}
	}

	/** Pulls the pickers back onto whatever the live session is actually using. */
	function syncPickers(): void {
		const s = session;
		if (!s) return;
		micSelect.value = s.inputDeviceId ?? "";
		if (outputSupported) speakerSelect.value = s.outputDeviceId ?? "";
	}

	micSelect.addEventListener("change", async () => {
		const id = micSelect.value || null;
		const s = session;
		// Before a connect the selection *is* the request: it is read again in
		// the connect handler and passed as `inputDeviceId`.
		if (!s) {
			logLine(`· next session will capture from ${id ?? "the system default"}`);
			return;
		}
		micSelect.disabled = true;
		try {
			await s.setInputDevice(id);
			// The sent track belongs to the graph's destination node, so swapping
			// the source changes nothing the peer connection can see.
			logLine(`· input device → ${id ?? "system default"} (no renegotiation)`);
		} catch (err) {
			// The SDK acquires the new capture before letting go of the old one,
			// so a failure leaves a working session on the previous device: put
			// the control back on the device that is actually running.
			logLine(`· input device failed, still on ${s.inputDeviceId ?? "system default"}: ${err}`);
			micSelect.value = s.inputDeviceId ?? "";
		} finally {
			micSelect.disabled = false;
		}
	});

	speakerSelect.addEventListener("change", async () => {
		const id = speakerSelect.value || null;
		const s = session;
		if (!s) {
			logLine(`· next session will play out of ${id ?? "the system default"}`);
			return;
		}
		try {
			await s.setOutputDevice(id);
			logLine(`· output device → ${id ?? "system default"}`);
		} catch (err) {
			// A refused sink (NotAllowedError) or an unsupported one: the element
			// keeps the sink it had, so the picker shows that one.
			logLine(`· output device failed, still on ${s.outputDeviceId ?? "system default"}: ${err}`);
			speakerSelect.value = s.outputDeviceId ?? "";
		}
	});

	function buildConfig(): Config {
		const cfg: Config = { systemPrompt: promptInput.value.trim(), language: "en" };
		const voice = voiceInput.value.trim();
		if (voice) cfg.voice = { id: voice };
		const first = firstInput.value.trim();
		if (first) cfg.firstMessage = first;
		// SPEC §10: spoken instead of `firstMessage` when the start carries
		// history, which is every resume. Empty resumes silently.
		const resumeMessage = resumeInput.value.trim();
		if (resumeMessage) cfg.resumeMessage = resumeMessage;
		// The idle trigger, so `reason:"idle"` can be produced by hand: set it to
		// a few seconds and stop talking. `suspendAfterSec` is `omitempty` on the
		// wire, so 0 is indistinguishable from absent and means the 300 s
		// default — a negative value is what disables the rule, which is why an
		// empty field sends no `idle` block at all rather than a zero.
		const idle = Number(idleInput.value.trim());
		if (idleInput.value.trim() !== "" && Number.isFinite(idle) && idle !== 0) {
			cfg.idle = { suspendAfterSec: idle };
		}
		const agent = agentField(agentInput.value);
		if (agent) cfg.agent = { url: agent.url };
		return cfg;
	}

	/** Tokens minted on this page, so the log says which mint a start used. */
	let mints = 0;

	/**
	 * Whether a level report has arrived for the current session. Monitoring
	 * going live is worth one line in the log — it is the proof the graph built
	 * — and nothing after that: ten reports a second would evict the other 199
	 * lines of frame log before the first turn ended. Reset on every connect.
	 */
	let levelSeen = false;

	/**
	 * The `onLevel` handler, passed only when `#meter` is ticked. It does almost
	 * nothing on purpose: the meter is painted by the rAF loop from
	 * `session.inputLevel`, and the one line it logs is the proof the graph
	 * built.
	 */
	function onLevelReport(l: InputLevel): void {
		if (levelSeen) return;
		levelSeen = true;
		logLine(`· input level monitoring live (first report ${l.db.toFixed(1)} dBFS)`);
	}

	/**
	 * The dev stand-in for the customer's backend (SPEC §8): POST the config to
	 * the node's own mint with the server API key and get back a token that
	 * carries it. In production this request happens on a backend the browser
	 * never sees the key of — which is exactly why the key is typed into a field
	 * here instead of being baked into the page.
	 *
	 * The SDK calls this on every `session.start` — first connect, every
	 * reconnect attempt and every resume — which is what makes a resume after a
	 * long idle suspend work: the 600 s TTL below has usually expired by then,
	 * and a stale token would be refused `unauthorized`. Each mint is logged, so
	 * "did the resume really re-mint?" is answerable by looking at the page.
	 */
	async function fetchToken(cfg: Config): Promise<string> {
		const mint = new URL(urlInput.value.trim().replace(/^ws/, "http"));
		mint.pathname = "/v1/tokens";
		const res = await fetch(mint, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${apiKeyInput.value.trim()}`,
			},
			body: JSON.stringify({ sub: "demo", ttlSeconds: 600, config: cfg }),
		});
		const body = (await res.json().catch(() => ({}))) as { token?: string; error?: string };
		if (!res.ok || !body.token) {
			throw new Error(`token mint failed (${res.status}): ${body.error ?? "no token"}`);
		}
		mints += 1;
		const agent = cfg.agent ? `, agent ${agentField(cfg.agent.url)?.host}` : "";
		logLine(`· minted session token #${mints}, ttl 600 s, ${body.token.length} chars${agent}`);
		return body.token;
	}

	controls.connect.addEventListener("click", async () => {
		if (session) return;
		view.clear();
		note.textContent = "";
		reason.textContent = "";
		levelSeen = false;
		// What `#meter` said at the moment the user asked for a session; the SDK
		// reads `onLevel` once, so a later tick cannot apply to this one.
		levelMonitored = meterCheck.checked;
		renderLevel(null);
		setMode("live");
		setStatus("connecting");
		try {
			// Read the form on every mint, not once at connect: the SDK
			// re-runs this callback on each start, so a config captured here
			// would make a resume after an edit re-mint the stale one. Live
			// smoke found a resume running on idleAfter=5m0s after #idle was
			// typed mid-session.
			const authed = apiKeyInput.value.trim() !== "";
			session = await Liaison.connect({
				url: urlInput.value.trim(),
				...(authed ? { token: () => fetchToken(buildConfig()) } : { config: buildConfig() }),
				// The SDK's half of §10, and the only half this page owns: the
				// idle window that produces the suspend belongs to the token.
				...(controls.wake.checked ? { wake: true } : {}),
				// The device the picker is sitting on, read at connect time so a
				// choice made before the first session is the one it opens with.
				// Omitted rather than sent as null when it says system default:
				// `exactOptionalPropertyTypes` makes the difference real.
				...(micSelect.value ? { inputDeviceId: micSelect.value } : {}),
				...(outputSupported && speakerSelect.value
					? { outputDeviceId: speakerSelect.value }
					: {}),
				// Passing `onLevel` is what turns monitoring on — there is no
				// separate flag — and with `wake` off it is also what makes the
				// SDK build an audio graph for a session that would not otherwise
				// have one. Which is why it is a checkbox: with `#meter` off this
				// page is the default path a consumer gets for free, and a page
				// that always asks for a graph cannot show that path at all.
				...(levelMonitored ? { onLevel: onLevelReport } : {}),
				onDevices: (d) => {
					logLine(`· devicechange — ${d.inputs.length} input(s), ${d.outputs.length} output(s)`);
					fillPickers(d);
				},
				tools: [setNote],
				onTranscript: (e) => {
					// A committed user turn is the one thing that says a wake was
					// real, so it is counted beside the wakes themselves.
					if (e.role === "user" && e.final && !e.amend) {
						counts.turns += 1;
						localStorage.setItem(WAKE_COUNTS_KEY, JSON.stringify(counts));
						renderCounts();
					}
					view.render(e);
				},
				onState: (s) => {
					setStatus(s);
					// A suspend or a close drops `s.agent` with no `state` frame.
					renderAgent();
					// Only a close may drop the object: `resume()` is a method on
					// it and renderJobs() reads `session?.jobs`, so a suspend —
					// which is a pause, not an end — keeps it.
					if (s === "closed") {
						session = null;
						renderJobs();
					}
					// The suspend note has served its purpose once words are
					// moving again; the raw-frame log keeps the record.
					if (s === "listening" || s === "thinking" || s === "speaking") {
						reason.textContent = "";
					}
					setMode(modeFor(s));
				},
				onSuspended: (r: SuspendReason) => {
					// The reasons are different UXes and a tester has to see
					// which one fired: "error" repairs itself (SPEC §13),
					// "client" and "replaced" (another tab took the
					// conversation) wait for the tap, and "idle" is the one
					// wake undoes — it waits for the tap *or* for speech.
					reason.dataset["r"] = r;
					const waiting =
						r === "idle" && controls.wake.checked
							? "listening for speech, or tap resume · mic still captured"
							: "tap resume";
					reason.textContent =
						r === "error"
							? "suspended · error · SDK is resuming itself"
							: `suspended · ${r} · ${waiting}`;
					logLine(`· session.suspended reason=${r}`);
				},
				onWake: () => {
					// The count §10 step 4 is after: every wake, with the
					// wall-clock time, kept across reloads.
					counts.wakes += 1;
					counts.lastWakeAt = Date.now();
					localStorage.setItem(WAKE_COUNTS_KEY, JSON.stringify(counts));
					renderCounts();
					// The payout starts when the woken leg connects and the
					// backlog only shrinks on its own clock, so follow it
					// rather than leaving a frozen number on the pane.
					followBacklog();
					const level = session?.lastWake;
					logLine(
						`· wake #${counts.wakes} at ${new Date(counts.lastWakeAt).toLocaleTimeString()}` +
							(level
								? ` — ${level.levelDb.toFixed(1)} dBFS over a ${level.floorDb.toFixed(1)} dBFS floor`
								: ""),
					);
				},
				onError: (e) => {
					view.line("error", "error", `${e.code}: ${e.message}`);
					// Non-fatal errors arrive here, and two of them move the
					// media state: a sticky input device that could not be
					// re-acquired (the SDK fell back to the default and cleared
					// the id) and an audio graph that would not build (the
					// session is up, just without levels). Both change what the
					// pickers should be showing.
					syncPickers();
				},
				onPlaybackClear: () => {
					logLine("· playback.clear — jitter buffer flushed");
				},
				onFrame: logFrame,
			});
			sid.textContent = session.sessionId;
			// `onState` has usually already put the page in `live`, but the mute
			// button and the pickers are painted from the session object, which
			// only exists as of this line.
			setMode(modeFor(session.state));
			startLevelPaint();
			// The grant exists now, so the browser will finally name the devices.
			void refreshDevices("after grant");
		} catch (err) {
			setStatus("error");
			view.line("error", "error", String(err));
			session = null;
			setMode("idle");
		}
	});

	controls.suspend.addEventListener("click", async () => {
		const s = session;
		if (!s) return;
		controls.suspend.disabled = true;
		await tapSuspend(s, view, logLine);
		setMode(modeFor(s.state));
	});

	controls.resume.addEventListener("click", async () => {
		const s = session;
		if (!s) return;
		// One tap at a time; a failure puts the button back through setMode.
		controls.resume.disabled = true;
		if (await tapResume(s, view, logLine)) {
			// A resume may land on a brand-new server-side session id.
			sid.textContent = s.sessionId;
			reason.textContent = "";
		}
		setMode(modeFor(s.state));
	});

	controls.end.addEventListener("click", () => {
		session?.end();
		session = null;
		reason.textContent = "";
		setMode("idle");
		renderJobs();
		renderAgent();
	});

	function submitText(): void {
		const text = controls.text.value.trim();
		if (!text || !session) return;
		session.sendText(text);
		controls.text.value = "";
	}

	function submitInject(): void {
		const text = controls.inject.value.trim();
		if (!text || !session) return;
		// `respond: true` is the omp bridge's shape: the note goes in as context
		// and the liaison speaks about it now rather than at the next turn.
		session.inject(text, { respond: true });
		controls.inject.value = "";
	}

	controls.send.addEventListener("click", submitText);
	controls.text.addEventListener("keydown", (ev) => {
		if (ev.key === "Enter") submitText();
	});
	controls.injectSend.addEventListener("click", submitInject);
	controls.inject.addEventListener("keydown", (ev) => {
		if (ev.key === "Enter") submitInject();
	});

	/**
	 * Tags the moment (SPEC §12). The note is *not* cleared: a run flags the
	 * same symptom repeatedly ("clipped me again"), and retyping it is how a
	 * mark gets pressed late or not at all.
	 */
	function submitMark(): void {
		if (!session) return;
		const note = controls.markNote.value.trim();
		session.mark("bad", note || undefined);
		marks++;
		markCount.textContent = marks === 1 ? "1 mark" : `${marks} marks`;
		logLine(`· mark: bad${note ? ` — ${note}` : ""}`);
	}

	controls.mark.addEventListener("click", submitMark);
	// The hotkey is the point of the feature: a verdict typed into a box while
	// the liaison is still talking is a verdict about something else by the
	// time it is sent. Ignored inside a field, where `m` is a letter — and
	// with a modifier held, where it belongs to the browser.
	document.addEventListener("keydown", (ev) => {
		if (ev.key !== "m" && ev.key !== "M") return;
		if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
		const on = ev.target;
		if (on instanceof HTMLInputElement || on instanceof HTMLTextAreaElement) return;
		if (controls.mark.disabled) return;
		ev.preventDefault();
		submitMark();
	});

	setMode("idle");
	renderJobs();
	renderCounts();
	setStatus("idle");
	renderLevel(null);
	// Enumerate before any grant: the entries are unnamed, so the pickers show
	// positional names, but the *count* is real and a device can be chosen for
	// the session that has not started yet.
	void refreshDevices("at load");
}

// The demo page is the only thing that bootstraps: test/demo.test.ts imports
// this module for the mode logic and the gestures, with no page in the document.
if (typeof document !== "undefined" && document.getElementById("connect")) bootstrap();
